import React, { useMemo, useCallback } from "react";

import "./VotingCard.css";
import HeaderElement from "../../HeaderElement/HeaderElement";
import VoteType from "./VoteType/VoteType";
import VoteButton from "../../Buttons/VoteButton/VoteButton";
import SendVote from "../../Buttons/SendVote/SendVote";
import popupicon from "../../../assets/icons/popup.svg";
import StandardPopup from "../../Global/StandardPopup/StandardPopup";
import requestHandler from "../../../utils/requestHandler";
import { UserAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import PopUpResponseButton from "../../Buttons/PopUpResponseButton/PopUpResponseButton";
import strings from "../../../locales/ro.json";
import statuteFromGroups from '../../../utils/statuteFromGroups'
import BarChartIcon from "@mui/icons-material/BarChart";
import Modal from "@mui/material/Modal";
import LoadingCircle from "../../Global/LoadingCircle";
import VoteStatusButton from "../../Buttons/VoteStatusButton/VoteStatusButton";


/*
 This is the voting card component, that contains the title, question, options of a ballot and a button for the user to vote

 Props:

 ballot = a ballot object that has the title,question, type and options fields populated according to the costelDB documentation
*/

const VotingCard = React.memo(({ ballot = { options: ["test1", "test2"] }, canVote=true , ballotId}) => {
  const navigate = useNavigate();
  const { user } = UserAuth();
  const myAuthToken = useMemo(() => `Bearer ${user.accessToken}`, [user.accessToken]);
  const [selectedVotes, setSelectedVotes] = React.useState([]);
  const [openClosingPopup, setOpenClosingPopup] = React.useState(false);
  const [openInvalidationPopup, setOpenInvalidationPopup] = React.useState(false);

  const [popupOpen, setPopupOpen] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false); 

  const [votersData, setVotersData] = React.useState({
    seniors: [],
    juniors: [],
    alumni: [],
    voters: null,
    requestsFinished: false,
    isLoading: true
  });

  // Memoize expensive calculations
  const hasVoted = useMemo(() => 
    ballot.question?.voters?.includes(user.name),
    [ballot.question?.voters, user.name]
  );

  const selectedVotesText = useMemo(() => 
    selectedVotes.map(voteIndex => `"${ballot?.question?.options[voteIndex]?.name}"`).join(", "),
    [selectedVotes, ballot?.question?.options]
  );

  const canShowAdminButtons = useMemo(() => 
    selectedVotes.length === 0 && user.isAdmin === true,
    [selectedVotes.length, user.isAdmin]
  );

  // Memoize callbacks
  const handleVoteClick = useCallback((optionNumber) => {
    if (!canVote || hasVoted) return;

    setSelectedVotes(prevVotes => {
      if (prevVotes.includes(optionNumber)) {
        return prevVotes.filter(vote => vote !== optionNumber);
      }
      
      if (ballot.question.is_multiple_choice === 0) {
        return [optionNumber];
      }
      
      return [...prevVotes, optionNumber];
    });
  }, [canVote, hasVoted, ballot.question.is_multiple_choice]);

  const closeBallot = useCallback(async () => {
    try {
      const requests = [
        requestHandler(`ballot/${ballotId}`, "PATCH", myAuthToken),
        requestHandler(`ballot/${ballotId}/viewgroup`, "PATCH", myAuthToken, { "view_group_id": 1 })
      ];

      if (ballot?.juniorBallotId) {
        requests.push(
          requestHandler(`ballot/${ballot.juniorBallotId.id}`, "PATCH", myAuthToken),
          requestHandler(`ballot/${ballot.juniorBallotId.id}/viewgroup`, "PATCH", myAuthToken, { "view_group_id": 1 })
        );
      }

      if (ballot?.alumniBallotId) {
        requests.push(
          requestHandler(`ballot/${ballot.alumniBallotId.id}`, "PATCH", myAuthToken),
          requestHandler(`ballot/${ballot.alumniBallotId.id}/viewgroup`, "PATCH", myAuthToken, { "view_group_id": 1 })
        );
      }

      await Promise.all(requests);
      navigate("/");
    } catch (error) {
      console.error("Error closing ballot:", error);
    }
  }, [ballotId, ballot?.juniorBallotId, ballot?.alumniBallotId, myAuthToken, navigate]);

  const deleteBallot = useCallback(async () => {
    try {
      const requests = [requestHandler(`ballot/${ballotId}`, "DELETE", myAuthToken)];

      if (ballot?.juniorBallotId) {
        requests.push(...Object.values(ballot.juniorBallotId).map(id => 
          requestHandler(`ballot/${id}`, "DELETE", myAuthToken)
        ));
      }

      if (ballot?.alumniBallotId) {
        requests.push(...Object.values(ballot.alumniBallotId).map(id => 
          requestHandler(`ballot/${id}`, "DELETE", myAuthToken)
        ));
      }

      await Promise.all(requests);
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error deleting ballot:", error);
    }
  }, [ballotId, ballot?.juniorBallotId, ballot?.alumniBallotId, myAuthToken, navigate]);

  const sendVote = useCallback(async () => {
    try {
      const optionIDs = selectedVotes.map(voteIndex => ballot.question.options[voteIndex].id);
      await requestHandler(`ballot/${ballotId}/vote`, "POST", myAuthToken, { optionIDs });
      setTimeout(() => navigate("/"), 500);
    } catch (error) {
      console.error("Error sending vote:", error);
    }
  }, [ballotId, ballot.question.options, myAuthToken, navigate, selectedVotes]);

  // Fetch voters data
  React.useEffect(() => {
    const fetchUsersData = async () => {
      if (!user?.accessToken) return;

      setVotersData(prev => ({ ...prev, isLoading: true }));
      try {
        const [users, voters] = await Promise.all([
          requestHandler('users', "GET", myAuthToken),
          requestHandler(`ballot/${ballotId}/voters`, "GET", myAuthToken)
        ]);

        if (users && voters?.voters) {
          const filterByStatute = (statute) => 
            users
              .filter(user => statuteFromGroups(user) === statute && voters.voters.includes(user.name))
              .map(user => user.name);

          setVotersData({
            seniors: filterByStatute("senior"),
            juniors: filterByStatute("junior"),
            alumni: filterByStatute("alumni"),
            voters,
            requestsFinished: true,
            isLoading: false
          });
        }
      } catch (error) {
        console.error("Error fetching users data:", error);
        setVotersData(prev => ({ ...prev, isLoading: false }));
      }
    };

    fetchUsersData();
  }, [ballotId, myAuthToken, user?.accessToken]);

  return (
    <div className="votingCardWrapper">
      <HeaderElement headerText={ballot.title} />
      <div className="metadataWrapper">
        <div className="ballotQuestion">{ballot.question.question_text}</div>
        <div className="onlyOneOption">{ballot.question.is_multiple_choice? "Exista mai multe variante de raspuns" : "Exista o singura varianta de raspuns"}</div>
        <VoteType voteType={ballot.showVotesPublicly == 0 ? 0 : 1} />
      </div>
      <div className="votingCardContainer">
        <Modal 
          open={openModal} 
          onClose={() => setOpenModal(false)}
        >
          <div className="voteStatusModalContainer">
            <div className="voteStatusModalVotersContainer">
              {votersData.isLoading ? (
                <LoadingCircle />
              ) : ballot && votersData.voters?.voters ? (
                <div className="voteStatusModalVoters">
                  <div className="voteStatusModalHeader">Au votat deja</div>
                  <div className="voteStatusModalCount">
                    {votersData.voters.voters.length} voturi
                  </div>
                  
                  {votersData.seniors.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        Seniori ({votersData.seniors.length}):
                      </div>
                      {votersData.seniors.map((senior, index) => (
                        <p key={`senior-${index}`}>{senior}</p>
                      ))}
                    </>
                  )}

                  {votersData.juniors.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        Juniori ({votersData.juniors.length}):
                      </div>
                      {votersData.juniors.map((junior, index) => (
                        <p key={`junior-${index}`}>{junior}</p>
                      ))}
                    </>
                  )}

                  {votersData.alumni.length > 0 && (
                    <>
                      <div className="voteStatusModalVotersStatute">
                        Alumni ({votersData.alumni.length}):
                      </div>
                      {votersData.alumni.map((alumni, index) => (
                        <p key={`alumni-${index}`}>{alumni}</p>
                      ))}
                    </>
                  )}
                </div>
              ) : (
                <div className="voteStatusModalVoters">
                  <div className="voteStatusModalHeader">Nu există voturi încă</div>
                </div>
              )}
            </div>

            <div className="voteStatusModalButton">
              <PopUpResponseButton
                type="cancel"
                text="Închide"
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </Modal>
        <StandardPopup message={strings.editvote.invalidatePopup} message2={selectedVotesText} icon={popupicon} yesFcn={async () => {setPopupOpen(false); await sendVote();}} noFcn={() => {setPopupOpen(false)}} isOpen={popupOpen}/>
        {ballot.question.options && <div className="voteButtonsContainer">
          {ballot.question.options.map((option, optionNumber) => {
            return (
              <VoteButton
                key={option.id || optionNumber}
                text={option.name}
                onClick={() => handleVoteClick(optionNumber)}
                isPressed={selectedVotes.includes(optionNumber)}
              />
            );
          })}
        </div>}
        <div className="sendVoteButtonContainer">
          {!hasVoted ? (
            selectedVotes.length > 0 && (
              <SendVote
                onClickFunction={() => {
                  setPopupOpen(true);
                }}
              />
            )
          ) : (!user.isAdmin && <div className="alreadyVoted">Ai votat deja</div>)}
          {
            canShowAdminButtons && (
              <div className="editBallotButtonsContainer">
                <PopUpResponseButton
                  type={"yes"}
                  text={"Inchide si afiseaza"}
                  onClick={() => {
                    setOpenClosingPopup(true);
                  }}
                />
                <PopUpResponseButton
                  type={"cancel"}
                  text={"Invalideaza"}
                  onClick={() => {
                    setOpenInvalidationPopup(true);
                  }}
                />
              </div>
            )
          }
          <StandardPopup message={strings.editvote.invalidatePopup} isOpen={openInvalidationPopup} yesFcn={deleteBallot} noFcn={() => {setOpenInvalidationPopup(false)}} icon={popupicon}/>
          <StandardPopup message={strings.editvote.showResultsPopup} isOpen={openClosingPopup} yesFcn={closeBallot} noFcn={() => {setOpenClosingPopup(false)}} icon={popupicon}/>
        </div>
        <div className="buttonsWrapper">
          <VoteStatusButton onClick={() => setOpenModal(true)} />
          {votersData.voters?.voters && !votersData.isLoading && (
            <div className="editBallotVotesCount">
              Total Voturi: {votersData.voters.voters.length}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

VotingCard.displayName = 'VotingCard';

export default VotingCard;
